import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { take } from 'rxjs/operators';

import { stateService } from './state';
import { viewService } from './view';
import { Subscription } from 'rxjs';

/**
 * @description Represents an image in the preload queue.
 */
interface QueueItem {
    image: string;
    onload: (safeUrl: SafeUrl | null) => void;
}

/**
 * @description Represents a cached image with its associated safe URL and object URL.
 */
interface ReadyItem {
    objturl: string | null;
    safeurl: SafeUrl | null;
}

/**
 * @description The `preloadService` is responsible for preloading and managing images in a queue. 
 * It handles caching, URL creation, and ensures images are loaded efficiently, even in offline scenarios.
 */
@Injectable({
    providedIn: 'root'
})
export class preloadService {
    private _queue: QueueItem[] = [];
    private _ready = new Map <string, ReadyItem> ();
    private _loading = false;
    private _loaded = false;

    private _ready_subscription: Subscription | null = null;

    /**
     * @param sanitizer The Angular `DomSanitizer` for creating safe URLs.
     * @param state The `stateService` providing application state and readiness events.
     * @param view The `viewService` providing online/offline state and events.
     */
    constructor(private sanitizer: DomSanitizer, private state: stateService, private view: viewService) {
        if (this.state.IsReady){
            this.Start();
        }
        else {
            this._ready_subscription = this.state.OnReady.subscribe(
            () => {
                this.Start();
            });        
        }
    }

    /**
     * @description Cleans up resources such as object URLs and subscriptions.
     */    
    OnDestroy() {
        for (const _queued of this._ready.values()) {
            if (_queued?.objturl) {
                URL.revokeObjectURL(_queued.objturl);
            }
        }
    
        if (this._ready_subscription) {
            this._ready_subscription.unsubscribe();
            this._ready_subscription = null;
        }
    }

    /**
     * @description Initializes the image loading process.
     */    
    private Start(){
        this._loaded = true;
        console.info("[PRELOAD] Loading images in background..")
        this._LoadImage();    
    }

    /**
     * @param url The URL of the image to recover.
     * @returns A `SafeUrl` if the image is ready, or `null` otherwise.
     */    
    Recover(url: string): SafeUrl | null{
        const _queued = this._ready.get(url);
        if (_queued){
            return _queued.safeurl;
        }

        return null;    // not loaded yet
    }

    /**
     * @description Adds an image to the preload queue and starts loading if conditions are met.
     * @param url The URL of the image to be preloaded.
     * @param onload Callback invoked when the image is loaded.
     */    
    Enqueue(url: string, onload: (safeUrl: SafeUrl | null) => void) {
        const _queued = this._ready.get(url);
        if (_queued){
            if (onload){
                onload(_queued.safeurl);
            }
        }

        const _included = this._queue.some(_queued => {
            return _queued.image == url;
        });

        if (!_included){
            this._queue.push({ image: url, onload: onload });
            if (this._loaded && !this._loading) {
                this._LoadImage();
            }    
        }
    }

    /**
     * @description Adds a blob to the ready map, creating safe and object URLs.
     * @param url The image URL.
     * @param blob The image blob.
     * @returns A `SafeUrl` for the image.
     */    
    private _addToReady(url: string, blob: Blob): SafeUrl | null {
        let _objturl = null;
        let _safeurl = null;
        
        _objturl = URL.createObjectURL(blob);
        if (_objturl){
            _safeurl = this.sanitizer.bypassSecurityTrustUrl(_objturl);
        }

        this._ready.set(url, {
            objturl: _objturl,
            safeurl: _safeurl
        });

        return _safeurl;
    }

    private _cachewarn = false;

    /**
     * @description Caches an image, either by fetching it or loading it from an existing cache.
     * @param _url The URL of the image.
     * @returns A promise that resolves to a `SafeUrl`, or `null` if the operation fails.
     */    
    private async _addToCache(_url: string): Promise<SafeUrl | null> {
        if  (/^data:image\/.*;base64,/.test(_url)){
            return _url;    // this is a base64 image
        }

        const _cachesearh = _url.startsWith('/assets') ? 'assets:assets:cache' : 'data:images:cache'; 

        const _keylist = await caches.keys();
        if (_keylist){
            let _cachenam = null;
            for (const _key of _keylist) {
                if (_key.indexOf(_cachesearh) != -1) {
                    _cachenam = _key;
                }
            }

            if (!_cachenam){
                if (!this._cachewarn){
                    console.warn("[PRELOAD] Images cache not found!")
                    this._cachewarn = true;   // notify only once
                }

                return false;
            }

            const _opencache = await caches.open(_cachenam);
            const _cachedres = await _opencache.match(_url, { ignoreMethod: true, ignoreVary: true });
            if (_cachedres?.ok) {
                const blob = await _cachedres.blob();
                return this._addToReady(_url, blob);
            }

            const response = await fetch(_url);
            if (response.ok) {
                await _opencache.put(_url, response.clone());
                const blob = await response.blob();
                return this._addToReady(_url, blob);
            }
        }

        return null;
    }

    /**
     * @description Processes the preload queue, ensuring images are loaded sequentially.
     */    
    private _LoadImage(): void {
        if (this._queue.length === 0) {
            this._loading = false;
            return;
        }

        if (this.view.IsOnline == false){
            this.view.OnOnline.pipe(take(1)).subscribe(
            () => {
                this._LoadImage();      // load next image (when online)
            });
        }
        else {
            this._loading = true;

            const _queued = this._queue[0];
            if (_queued){
                const _image = _queued.image;
                const _ready = this._ready.get(_image);
                if (_ready){
                    if (_queued.onload){
                        _queued.onload(_ready.safeurl);
                    }

                    this._queue.shift();        // remove from queued
                    this._LoadImage();          // load next image                    
                } 
                else {
                    this._addToCache(_image).then(
                    safeurl => {
                        if (safeurl){     
                            if (_queued.onload){
                                _queued.onload(safeurl);
                            }
                        }    

                        this._queue.shift();    // remove from queued
                        this._LoadImage();      // load next image                    
                    });        
                }
            }    
        }
    }
}
